import RecordPaymentModal from "@/features/enrollments/components/RecordPaymentModal"
import { useInvalidateEnrollments } from "@/hooks/api/useEnrollments"
import { useUpdateInvoice } from "@/hooks/api/useInvoices"
import { usePaymentInstructions } from "@/hooks/api/usePaymentInstructions"
import { useCustomToast } from "@/hooks/useCustomToast"
import type { EnrollmentResponse } from "@/types/enrollment"
import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useState } from "react"

interface InvoiceDetailModalProps {
  enrollment: EnrollmentResponse | null
  isOpen: boolean
  onClose: () => void
  onPaymentSuccess?: () => void
}

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return "N/A"
  return `$${Number(amount).toFixed(2)}`
}

const formatDate = (date: string | null | undefined): string => {
  if (!date) return "N/A"
  const dateObj = new Date(date)
  if (Number.isNaN(dateObj.getTime())) return "N/A"
  return dateObj.toLocaleDateString()
}

export const InvoiceDetailModal = ({
  enrollment,
  isOpen,
  onClose,
  onPaymentSuccess,
}: InvoiceDetailModalProps) => {
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const updateInvoice = useUpdateInvoice()
  const invalidateEnrollments = useInvalidateEnrollments()
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const { data: paymentInstructions } = usePaymentInstructions()

  if (!enrollment || !enrollment.invoice) {
    return null
  }

  const invoice = enrollment.invoice
  const isPaid = invoice.payment_status === "paid"
  const isPartiallyPaid = invoice.payment_status === "partial"
  const balance = (invoice.amount_due || 0) - (invoice.amount_paid || 0)

  const handlePaymentSubmit = async (
    _enrollmentId: string,
    paymentData: any,
  ) => {
    try {
      if (!enrollment.invoice?.id) {
        showErrorToast("Invoice not found for this enrollment")
        return
      }

      const currentlyPaid = enrollment.invoice.amount_paid ?? 0
      const newTotalPaid = currentlyPaid + paymentData.amount_paid

      // Update invoice with payment information
      await updateInvoice.mutateAsync({
        invoiceId: enrollment.invoice.id,
        invoiceData: {
          amount_paid: newTotalPaid,
          payment_method: paymentData.payment_method,
          payment_date: paymentData.payment_date,
          external_payment_id: paymentData.external_payment_id || null,
          notes: paymentData.notes || enrollment.invoice.notes || null,
        },
      })

      showSuccessToast(
        `Successfully recorded payment of $${paymentData.amount_paid.toFixed(2)}`,
      )
      setShowPaymentForm(false)
      invalidateEnrollments()

      if (onPaymentSuccess) {
        onPaymentSuccess()
      }
    } catch (error) {
      showErrorToast("Failed to record payment. Please try again.")
    }
  }

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size={{ base: "full", md: "lg" }}
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <DialogBackdrop />
      <DialogContent maxH="90vh" display="flex" flexDirection="column">
        <DialogHeader>
          <DialogTitle>Invoice Details</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody overflowY="auto" flex="1">
          <VStack gap={4} alignItems="stretch">
            {/* Invoice Header */}
            <Box>
              <Text fontSize="sm" color="gray.600">
                Invoice Number
              </Text>
              <Text fontSize="lg" fontWeight="semibold">
                {invoice.invoice_number || "Not generated"}
              </Text>
            </Box>

            <Separator />

            {/* Enrollment Details */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                Enrollment Details
              </Text>
              <VStack gap={1} alignItems="stretch">
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Student ID:
                  </Text>
                  <Text fontSize="sm">{enrollment.student_id}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Class:
                  </Text>
                  <Text fontSize="sm">{enrollment.class_template_id}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Session:
                  </Text>
                  <Text fontSize="sm">{enrollment.session_id}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Status:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {enrollment.status || enrollment.effective_status}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Separator />

            {/* Payment Details */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>
                Payment Summary
              </Text>
              <VStack gap={2} alignItems="stretch">
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Amount Due:
                  </Text>
                  <Text fontSize="md" fontWeight="semibold">
                    {formatCurrency(invoice.amount_due)}
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Amount Paid:
                  </Text>
                  <Text
                    fontSize="md"
                    color={
                      isPaid
                        ? "green.600"
                        : isPartiallyPaid
                          ? "orange.600"
                          : "gray.600"
                    }
                  >
                    {formatCurrency(invoice.amount_paid)}
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Balance:
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={balance > 0 ? "red.600" : "green.600"}
                  >
                    {formatCurrency(balance)}
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">
                    Payment Status:
                  </Text>
                  <Box
                    px={3}
                    py={1}
                    borderRadius="md"
                    bg={
                      isPaid
                        ? "green.100"
                        : isPartiallyPaid
                          ? "orange.100"
                          : "gray.100"
                    }
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={
                        isPaid
                          ? "green.700"
                          : isPartiallyPaid
                            ? "orange.700"
                            : "gray.700"
                      }
                    >
                      {invoice.payment_status?.toUpperCase() || "PENDING"}
                    </Text>
                  </Box>
                </HStack>
              </VStack>
            </Box>

            {/* Payment Information (if paid) */}
            {(invoice.payment_method || invoice.payment_date) && (
              <>
                <Separator />
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Payment Information
                  </Text>
                  <VStack gap={1} alignItems="stretch">
                    {invoice.payment_method && (
                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Method:
                        </Text>
                        <Text fontSize="sm">
                          {invoice.payment_method
                            .replace("_", " ")
                            .toUpperCase()}
                        </Text>
                      </HStack>
                    )}
                    {invoice.payment_date && (
                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Date:
                        </Text>
                        <Text fontSize="sm">
                          {formatDate(invoice.payment_date)}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              </>
            )}

            {/* Notes */}
            {(invoice.notes || enrollment.notes) && (
              <>
                <Separator />
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Notes
                  </Text>
                  <Text fontSize="sm" color="gray.700">
                    {invoice.notes || enrollment.notes}
                  </Text>
                </Box>
              </>
            )}

            {/* Payment Instructions (if unpaid) */}
            {balance > 0 && paymentInstructions && (
              <>
                <Separator />
                <Box
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.200"
                  borderRadius="md"
                  p={4}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                    color="blue.800"
                  >
                    Payment Instructions
                  </Text>

                  {/* Zelle Payment */}
                  <Box mb={3}>
                    <Text fontSize="sm" fontWeight="medium" color="blue.800">
                      Pay via Zelle (Fastest)
                    </Text>
                    <VStack alignItems="stretch" gap={0} ml={3} mt={1}>
                      <Text fontSize="sm" color="blue.700">
                        Send to: {paymentInstructions.zelle_email}
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Amount: {formatCurrency(balance)}
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Reference: Include "{invoice.invoice_number}" in memo
                      </Text>
                    </VStack>
                  </Box>

                  {/* Cash/Check Payment */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="blue.800">
                      Pay by Cash or Check
                    </Text>
                    <VStack alignItems="stretch" gap={0} ml={3} mt={1}>
                      <Text fontSize="sm" color="blue.700">
                        Visit: {paymentInstructions.office_address}
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Hours: {paymentInstructions.office_hours}
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Checks payable to:{" "}
                        {paymentInstructions.check_payable_to}
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Write invoice # on check: {invoice.invoice_number}
                      </Text>
                    </VStack>
                  </Box>

                  <Text fontSize="xs" color="blue.600" mt={3}>
                    Allow 1-2 business days for payment confirmation.
                  </Text>
                </Box>
              </>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <HStack gap={3}>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {balance > 0 && (
              <Button
                colorPalette="blue"
                onClick={() => setShowPaymentForm(true)}
              >
                Make Payment
              </Button>
            )}
          </HStack>
        </DialogFooter>
      </DialogContent>

      {/* Payment Form Modal */}
      <RecordPaymentModal
        enrollment={enrollment}
        isOpen={showPaymentForm}
        onClose={() => setShowPaymentForm(false)}
        onSubmit={handlePaymentSubmit}
        isSubmitting={updateInvoice.isPending}
      />
    </DialogRoot>
  )
}

export default InvoiceDetailModal
